import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import {
    WithdrawalHistoryHeader,
    WithdrawalHistoryWrapper,
    ChevronDownWrapper,
} from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory/WithdrawalHistory.style';
import { AutomaticWithdrawalHistory } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory/WithdrawalHistory/AutomaticWithdrawalHistory';
import { AutomaticWithdrawalState } from 'src/AppState/AutomaticWithdrawalState';
import { AutoWithdrawalHistoryResponseLKType } from 'src/api/config/automaticWithdrawal/getWithdrawalHistoryLK';
import { StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { Common, useCommon } from 'src/AppState/Common';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { netDeposits } from 'src_server/types/wallet';
import {
    BankTransferPaymentMethodSchema,
    BankTransferPaymentMethodSchemaType,
    FullPaymentMethodSchemaType,
    FullPaymentMethodsSchema,
    PaymentMethodObjectSchemaType,
    TempInvalidPaysafeWithoutCardSchema,
} from 'src_server/types/wallet/netDeposits';
import { convertToNumber } from 'src_common/common/mobx-utils/Form2/validators';
import { Amount } from 'src_common/common/amount/Amount';
import { isAfter, isBefore } from 'date-fns';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { CurrencySign } from 'src/utils/format';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

export type { GroupByActiveCardsType } from './PaymentProcessing';

interface HeaderAndSearchPropsType {
    accountId: number;
}

export type ProcessedNetDepositFullType = {
    aggregations: netDeposits.SuccessResponseType['results'][number]['aggregations'];
    paymentMethod: FullPaymentMethodSchemaType;
};
export type ProcessedNetDepositBTType = {
    aggregations: netDeposits.SuccessResponseType['results'][number]['aggregations'];
    paymentMethod: BankTransferPaymentMethodSchemaType;
};

export type NetDepositFullType = {
    aggregations: netDeposits.SuccessResponseType['results'][number]['aggregations'];
    paymentMethod: FullPaymentMethodSchemaType;
};
export type NetDepositBTType = {
    aggregations: netDeposits.SuccessResponseType['results'][number]['aggregations'];
    paymentMethod: BankTransferPaymentMethodSchemaType;
};

export type CombinedType = ProcessedNetDepositFullType | ProcessedNetDepositBTType;

export class NetDepositState {
    public readonly netDepositResource: Resource<netDeposits.ResponseType>;
    public readonly models: ModelsState;
    public readonly configState: ConfigState;
    public showExpiredCards: boolean = false;
    public customerId: number;
    public constructor(customerId: number, common: Common, models: ModelsState) {
        this.netDepositResource = new Resource(async (): Promise<netDeposits.ResponseType> => {
            return await common.trpcClient.client.walletRouter.netDeposits.query({ accountId: customerId });
        });
        this.models = models;
        this.customerId = customerId;
        this.configState = ConfigState.get(common);
        makeAutoObservable(this);
    }

    public onShowExpiredCards = (): void => {
        this.showExpiredCards = !this.showExpiredCards;
    };

    public get netDeposits(): netDeposits.SuccessResponseType['results'] {
        const response = this.netDepositResource.getReady();
        if (response === null || response.responseStatus === 'error') {
            return [];
        }

        return response.data.results.sort((a: PaymentMethodObjectSchemaType, b: PaymentMethodObjectSchemaType) => {
            const converter = convertToNumber('Invalid total amount');
            const ATotalAmount = converter(a.aggregations.totalAmount);
            const BTotalAmount = converter(b.aggregations.totalAmount);

            if (ATotalAmount.value.type === 'error' || BTotalAmount.value.type === 'error') {
                return 1;
            }
            return BTotalAmount.value.data - ATotalAmount.value.data;
        });
    }

    public get walletDataCurrency(): CurrencyType | undefined {
        return this.models.getUserModel(this.customerId).walletData.data?.currency;
    }

    public getNetDepositAmount = (netDepositAllDeposits: CombinedType[]): Amount => {
        const initialAmount = new Amount('0');

        return netDepositAllDeposits.reduce((acc, curVal) => {
            const currentAmount = new Amount(curVal.aggregations.totalAmount);
            return acc.add(currentAmount);
        }, initialAmount);
    };

    public getNetDepositFormattedSum = (netDepositAllDeposits: CombinedType[]): string => {
        const amountSum = this.getNetDepositAmount(netDepositAllDeposits);
        if (amountSum.value === '0') {
            return `${CurrencySign(this.walletDataCurrency)} --`;
        }
        return this.getNetDepositAmount(netDepositAllDeposits).format(this.walletDataCurrency ?? 'GBP', {
            withoutZeros: true,
        });
    };

    public netDepositSum = (netDepositAllDeposits: CombinedType[]): number => {
        const sum = this.configState.precision.valueOldFormat(this.getNetDepositAmount(netDepositAllDeposits));
        return sum;
    };

    public isExpiredCardsExist = (expiredCards: CombinedType[]): boolean => {
        return expiredCards.length > 0;
    };

    public get netDepositsBankItems(): ProcessedNetDepositFullType[] {
        const processed: ProcessedNetDepositFullType[] = [];
        for (const item of this.netDeposits) {
            const firstPaymentMethod = item.paymentMethods[0];
            const singleCheck = FullPaymentMethodsSchema.safeParse(firstPaymentMethod);
            if (singleCheck.success) {
                if (item.paymentMethods.length > 1) {
                    processed.push({
                        aggregations: item.aggregations,
                        paymentMethod: { ...singleCheck.data, expires: null },
                    });
                } else {
                    processed.push({
                        aggregations: item.aggregations,
                        paymentMethod: singleCheck.data,
                    });
                }
            }
        }
        return processed;
    }

    public get netDepositsBankTrasferItems(): ProcessedNetDepositBTType[] {
        const processed: ProcessedNetDepositBTType[] = [];
        for (const item of this.netDeposits) {
            const paymentMethod = BankTransferPaymentMethodSchema.safeParse(item.paymentMethods[0]);
            if (paymentMethod.success) {
                processed.push({
                    aggregations: item.aggregations,
                    paymentMethod: paymentMethod.data,
                });
            }
            const tempInvalidPaysafePayment = TempInvalidPaysafeWithoutCardSchema.safeParse(item.paymentMethods[0]);
            if (tempInvalidPaysafePayment.success) {
                processed.push({
                    aggregations: item.aggregations,
                    paymentMethod: { id: tempInvalidPaysafePayment.data.id },
                });
            }
        }
        return processed;
    }

    public get allPaymentMethods(): CombinedType[] {
        return [...this.netDepositsBankItems, ...this.netDepositsBankTrasferItems];
    }

    public get activeOrBankTransfer(): CombinedType[] {
        const activeCards = this.netDepositsBankItems
            .filter((el) =>
                el.paymentMethod.expires !== null && el.paymentMethod.expires !== undefined
                    ? isAfter(new Date(el.paymentMethod.expires), new Date())
                    : true
            )
            .sort((a, b) =>
                isAfter(new Date(a.aggregations.firstTransaction), new Date(b.aggregations.firstTransaction)) ? 1 : -1
            );
        return [...activeCards, ...this.netDepositsBankTrasferItems];
    }

    public get expiredCards(): ProcessedNetDepositFullType[] {
        return this.netDepositsBankItems.filter((el) =>
            el.paymentMethod.expires !== null && el.paymentMethod.expires !== undefined
                ? isBefore(new Date(el.paymentMethod.expires), new Date())
                : false
        );
    }
}

class WithdrawalHistoryState {
    public isCollapsed: boolean = true;
    public history: null | AutoWithdrawalHistoryResponseLKType = null;
    public isBlurred: boolean = false;
    private readonly customerId: number;
    public readonly netDepositState: NetDepositState;

    public constructor(
        private readonly automaticWithdrawalState: AutomaticWithdrawalState,
        private readonly accountId: number,
        private readonly models: ModelsState,
        private readonly common: Common
    ) {
        this.automaticWithdrawalState = automaticWithdrawalState;
        this.customerId = this.accountId;

        this.netDepositState = new NetDepositState(this.customerId, this.common, this.models);

        makeAutoObservable(this);
    }

    private async getHistory(): Promise<void> {
        const userId = this.customerId;
        this.history = await this.automaticWithdrawalState.getHistory(userId);
    }

    public toggleHistory = async (): Promise<void> => {
        if (this.isCollapsed) {
            this.isBlurred = true;
            await this.netDepositState.netDepositResource.refresh();
            await this.getHistory();
            this.isCollapsed = false;
            this.isBlurred = false;
        } else {
            this.isCollapsed = true;
        }
    };

    public get walletDataLocal(): StaffAccountWalletDataType | null {
        return this.models.getUserModel(this.customerId).walletData.data;
    }
}

export const WithdrawalHistory = observer(({ accountId }: HeaderAndSearchPropsType) => {
    const { automaticWithdrawalState, models } = useAppStateContext();
    const common = useCommon();

    const [state] = React.useState(
        () => new WithdrawalHistoryState(automaticWithdrawalState, accountId, models, common)
    );

    return (
        <>
            <WithdrawalHistoryHeader
                onClick={state.toggleHistory}
                isBlurred={state.isBlurred}
            >
                <ChevronDownWrapper isCollapsed={state.isCollapsed} />
                Withdrawal History
            </WithdrawalHistoryHeader>
            <WithdrawalHistoryWrapper isCollapsed={state.isCollapsed}>
                <AutomaticWithdrawalHistory
                    customerId={accountId}
                    history={state.history}
                    netDepositState={state.netDepositState}
                    walletDataLocal={state.walletDataLocal}
                />
            </WithdrawalHistoryWrapper>
        </>
    );
});
